// Insights Workshop copy of frontend/src/playground/charts.tsx, ported from the Insights Lab at
// 12749ec1 (2026-10-08) and repointed at /api/v1/workshop. Not shared with the
// Lab on purpose (see src/workshop/InsightsWorkshop.tsx); fix both if a fix
// applies to both.
/**
 * Insights Playground — client-side visuals over a query result.
 *
 * A result is columns + rows straight from DuckDB. The visuals here infer
 * what each column can do (measure, category, coordinate) and draw with
 * plain SVG: table, bar, line, scatter, and a pitch map. No chart library —
 * the dependency policy makes one a decision, and the MVP does not need it.
 *
 * Color follows the dataviz method: six categorical slots in fixed order
 * (never cycled — a 7th series folds into "other"), validated for both the
 * light and dark surfaces of this app's theme (2026-09-01, palette
 * validator: all checks pass; light mode carries a contrast WARN on three
 * slots, relieved by the result table that always renders under the chart).
 */
import { useState } from 'react'
import {
  DEFAULT_LENGTH_M, PEN_AREA_HALF_WIDTH_M, edgesFromGoalLineM, edgesM,
} from '../../lib/pitchZones'

export type Column = { name: string; type: string }
export type Cell = string | number | boolean | null | unknown[] | Record<string, unknown>
export type Result = { columns: Column[]; rows: Cell[][]; spatial?: SpatialHint }

export type VisualKind =
  | 'table' | 'bar' | 'line' | 'scatter' | 'pitch'
  | 'heatmap' | 'flow' | 'histogram' | 'pitchzones'
  | 'shape' | 'spacemap' | 'timeline' | 'passnet' | 'rose'

export type Mapping = {
  x?: string
  y?: string
  /** End coordinates, for start-to-end traces (flow). */
  x2?: string
  y2?: string
  series?: string
  size?: string
  label?: string
  /** Small-multiple grouping (shape): one mini pitch per distinct value. */
  facet?: string
  /** 'show' = print each bar's value on the chart (bar), not only in the
   *  hover tooltip. A string, not a boolean, so it rides the same
   *  Record<string, string> the rest of the mapping serializes through. */
  values?: string
}

export const VISUALS: { kind: VisualKind; label: string; hint: string }[] = [
  { kind: 'table', label: 'Table', hint: 'Every row as returned.' },
  { kind: 'bar', label: 'Bar', hint: 'One measure per category.' },
  { kind: 'line', label: 'Line', hint: 'A measure over an ordered x (frame, time).' },
  { kind: 'scatter', label: 'Scatter', hint: 'Two measures; optional series and size.' },
  { kind: 'pitch', label: 'Pitch map', hint: 'x/y in metres on a pitch, optional series and size.' },
  { kind: 'heatmap', label: 'Heat map', hint: 'Density of x/y points on the pitch.' },
  { kind: 'flow', label: 'Ball paths', hint: 'Start-to-end traces on the pitch.' },
  { kind: 'histogram', label: 'Histogram', hint: 'Distribution of one measure.' },
  { kind: 'pitchzones', label: 'Pitch zones', hint: 'Named regions (zones, thirds, halves, wings) shaded on the pitch — no coordinates needed.' },
  { kind: 'shape', label: 'Team shape', hint: 'Average positions on the pitch (attacking right): dots, the shape hull, and its measured width and depth. A facet column draws small multiples.' },
  { kind: 'spacemap', label: 'Space map', hint: 'Where players are NOT: inverse occupancy — brighter cells were left unoccupied.' },
  { kind: 'timeline', label: 'Timeline', hint: 'Colored spans across the match clock — a categorical state (formation, phase) and when it held.' },
  { kind: 'passnet', label: 'Pass network', hint: 'Players at their average positions, connected by pass edges weighted by count.' },
  { kind: 'rose', label: 'Direction rose', hint: 'Angular distribution of movements — petals point where the ball went, attacking right.' },
]

const LIGHT = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#4a3aa7']
const DARK = ['#3987e5', '#d95926', '#199e70', '#c98500', '#d55181', '#9085e9']
const OTHER = { light: '#8a8a86', dark: '#7c7f85' }

export function palette(): string[] {
  const dark = typeof document !== 'undefined'
    && document.documentElement.getAttribute('data-theme') === 'dark'
  return dark ? DARK : LIGHT
}
function otherColor(): string {
  const dark = typeof document !== 'undefined'
    && document.documentElement.getAttribute('data-theme') === 'dark'
  return dark ? OTHER.dark : OTHER.light
}

// ── column inference ───────────────────────────────────────────────────────

const NUMERIC = /^(TINYINT|SMALLINT|INTEGER|BIGINT|HUGEINT|UTINYINT|USMALLINT|UINTEGER|UBIGINT|FLOAT|DOUBLE|DECIMAL|REAL)/i

export function isMeasure(c: Column): boolean {
  return NUMERIC.test(c.type)
}

/** The server's spatial contract, when the result carried one. Mirrors
 *  lib/projects SpatialContract; typed structurally here so this module
 *  keeps zero page-layer imports. */
export type SpatialHint = {
  roles: Partial<Record<'x' | 'y' | 'x2' | 'y2' | 'team' | 'player' | 'frame' | 'size', string>>
  frame: {
    origin: 'center' | 'corner'; units: 'm' | 'cm'; units_divisor: number
    /** Layer-stack rows: how each side attacks — 'canonical' (home +x in
     *  every half), 'attacking' (every row's own team +x), 'camera' (as
     *  filmed). Absent for legacy tables. */
    view?: 'canonical' | 'attacking' | 'camera'
  }
} | null

/** Mapping for the spatial kinds straight from the server's declared roles —
 *  the writer's own column names, not a name-pattern guess. Returns null
 *  when the contract cannot serve this kind, and the regexes take over. */
function mappingFromRoles(kind: VisualKind, spatial: SpatialHint, cols: Column[]): Mapping | null {
  const roles = spatial?.roles
  if (!roles?.x || !roles.y) return null
  const has = (n?: string) => (n && cols.some((c) => c.name === n) ? n : undefined)
  const x = has(roles.x), y = has(roles.y)
  if (!x || !y) return null
  const series = has(roles.team) ?? has(roles.player)
  switch (kind) {
    case 'pitch':
      return { x, y, series, size: has(roles.size), label: has(roles.player) }
    case 'heatmap':
    case 'spacemap':
      return { x, y }
    case 'flow':
    case 'rose': {
      const x2 = has(roles.x2), y2 = has(roles.y2)
      if (!x2 || !y2) return null
      return { x, y, x2, y2, series }
    }
    case 'shape':
      return { x, y, label: has(roles.player), series: has(roles.team) }
    default:
      return null
  }
}

/** Per-team, per-half attack signs from the server
 *  (playground.orientation): multiplying a team's x by
 *  signs[team][phase] redraws it attacking +x. */
export type OrientationInfo = {
  source: string | null
  signs: Record<string, Record<string, number>>
  half2_frame: number | null
}

/** The same rows with corner-origin coordinates (the layer stack's x_m /
 *  *_att_m, 105x68) moved to the centre-spot origin every pitch and the
 *  orientation rotation assume: x - 52.5, y - 34 — a shift, never a flip,
 *  so `-v` is again a rotation about the centre spot. The stack's y = 0 is
 *  the attacker's right touchline (l3/space.py), so +y stays the attacker's
 *  left, as it is in the centre-origin tables. Centre-origin results come
 *  back unchanged. */
export function centreOrigin(result: Result): Result {
  const s = result.spatial
  if (!s || s.frame.origin !== 'corner') return result
  const at = (r: 'x' | 'y' | 'x2' | 'y2') =>
    s.roles[r] ? idx(result.columns, s.roles[r]!) : -1
  const xs = [at('x'), at('x2')].filter((i) => i >= 0)
  const ys = [at('y'), at('y2')].filter((i) => i >= 0)
  const rows = result.rows.map((row) => row.map((v, i) => {
    const n = num(v)
    if (n === null) return v
    if (xs.includes(i)) return n - 52.5
    if (ys.includes(i)) return n - 34
    return v
  }))
  return { ...result, rows,
           spatial: { ...s, frame: { ...s.frame, origin: 'center' } } }
}

/** Rows redrawn to the house rule: OUR marks attack +x (right on a
 *  horizontal pitch, up on the vertical board), the opponent's attack -x
 *  (Ben, 2026-09-03). A 180-degree rotation per row — both coordinates
 *  negated — never a mirror, which would swap left-footed geometry.
 *
 *  Returns null when the rule cannot be applied honestly: no team column
 *  in the result, no measured signs, an unresolved "us", or a two-half
 *  game without a frame column to phase the rows. The caller then draws
 *  the data as recorded rather than guessing. */
export function orientRows(
  result: Result,
  mapping: Mapping,
  orientation: OrientationInfo | null,
  ourTeam: number | null,
): Cell[][] | null {
  const roles = result.spatial?.roles
  const view = result.spatial?.frame.view
  // Camera-view rows switch ends per L3's rotation, which the signs do not
  // carry: drawn as filmed rather than guessed.
  if (view === 'camera') return null
  if (!roles?.team || !orientation?.source || ourTeam === null) return null
  const teamIdx = idx(result.columns, roles.team)
  if (teamIdx < 0) return null
  const half2 = orientation.half2_frame
  const frameIdx = roles.frame ? idx(result.columns, roles.frame) : -1
  if (half2 !== null && frameIdx < 0) return null

  const coordIdx = [mapping.x, mapping.y, mapping.x2, mapping.y2]
    .map((n) => idx(result.columns, n))
    .filter((i) => i >= 0)
  if (!coordIdx.length) return null

  return result.rows.map((row) => {
    const t = num(row[teamIdx] ?? null)
    if (t === null) return row
    const phase = half2 !== null && (num(row[frameIdx] ?? null) ?? 0) >= half2 ? '2' : '1'
    // Attacking-view rows already have every team attacking +x.
    const sign = view === 'attacking' ? 1 : orientation.signs[String(t)]?.[phase]
    if (sign === undefined) return row
    const f = sign * (t === ourTeam ? 1 : -1)
    if (f === 1) return row
    return row.map((v, i) => {
      if (!coordIdx.includes(i)) return v
      const n = num(v)
      return n === null ? v : -n
    })
  })
}

/** Columns that look like pitch coordinates: the server's contract first
 *  (it knows the writers and the units), name patterns as the fallback —
 *  the operator can always override in the mapping controls. */
export function guessMapping(kind: VisualKind, cols: Column[], spatial?: SpatialHint): Mapping {
  const fromRoles = mappingFromRoles(kind, spatial ?? null, cols)
  if (fromRoles) return fromRoles
  const measures = cols.filter(isMeasure).map((c) => c.name)
  const dims = cols.filter((c) => !isMeasure(c)).map((c) => c.name)
  const byName = (re: RegExp) => cols.find((c) => re.test(c.name))?.name
  switch (kind) {
    case 'bar':
      return { x: dims[0] ?? cols[0]?.name, y: measures[0] }
    case 'line':
      return { x: byName(/^(frame|framecount|f|t|time|secs|second|minute)$/i) ?? measures[0],
               y: measures.find((m) => m !== byName(/^(frame|framecount|f|t|time|secs|second|minute)$/i)) ?? measures[1],
               series: dims[0] }
    case 'scatter':
      return { x: measures[0], y: measures[1], series: dims[0] }
    case 'pitch':
      return { x: byName(/(^|_)(x|ball_x|start_x|pos_x)$/i) ?? measures[0],
               y: byName(/(^|_)(y|ball_y|start_y|pos_y)$/i) ?? measures[1],
               series: byName(/^(team|team_id|side|player|jersey|player_id)$/i) ?? dims[0],
               size: byName(/(^|_)(cci_score|cci|xg|score|weight|xd)$/i),
               label: byName(/^(player_id|PlayerID|jersey|name|label)$/i) }
    case 'heatmap':
      return { x: byName(/(^|_)(x|ball_x|start_x|pos_x)$/i) ?? measures[0],
               y: byName(/(^|_)(y|ball_y|start_y|pos_y)$/i) ?? measures[1] }
    case 'flow':
      return { x: byName(/(^|_)start_x$/i) ?? byName(/(^|_)x$/i) ?? measures[0],
               y: byName(/(^|_)start_y$/i) ?? byName(/(^|_)y$/i) ?? measures[1],
               x2: byName(/(^|_)end_x$/i) ?? measures[2],
               y2: byName(/(^|_)end_y$/i) ?? measures[3],
               series: byName(/^(team|team_id|side|outcome|result)$/i) ?? dims[0] }
    case 'histogram':
      return { x: byName(/(^|_)(cci_score|cci|xg|score|duration\w*|speed|distance|intensity)$/i)
                  ?? measures[0] }
    case 'pitchzones':
      return { x: byName(/^(zone|field_zone|channel|region|segment)$/i) ?? dims[0],
               y: byName(/^(count|n|pct|avg_\w+)$/i) ?? measures[0] }
    case 'shape':
      return { x: byName(/^(x|avg_x)$/i) ?? measures[0],
               y: byName(/^(y|avg_y)$/i) ?? measures[1],
               label: byName(/^(player_id|PlayerID|jersey|name)$/i),
               series: byName(/^(team|team_id|side)$/i),
               facet: byName(/^(ball_channel|ball_zone|facet|group|segment|half)$/i) }
    case 'spacemap':
      return { x: byName(/(^|_)(x|pos_x)$/i) ?? measures[0],
               y: byName(/(^|_)(y|pos_y)$/i) ?? measures[1] }
    case 'timeline': {
      const cat = byName(/^(formation|state|category|label|phase)$/i) ?? dims[0]
      return { x: byName(/^(start_minute|start_min|from_minute|start)$/i) ?? measures[0],
               x2: byName(/^(end_minute|end_min|to_minute|end)$/i) ?? measures[1],
               label: cat,
               series: dims.find((d) => d !== cat) }
    }
    case 'passnet':
      return { x: byName(/^from_x$/i) ?? measures[0],
               y: byName(/^from_y$/i) ?? measures[1],
               x2: byName(/^to_x$/i) ?? measures[2],
               y2: byName(/^to_y$/i) ?? measures[3],
               label: byName(/^(from_player|from|passer)$/i),
               series: byName(/^(to_player|to|receiver)$/i),
               size: byName(/^(n|count|passes)$/i) }
    case 'rose':
      return { x: byName(/(^|_)start_x$/i) ?? byName(/(^|_)x$/i) ?? measures[0],
               y: byName(/(^|_)start_y$/i) ?? byName(/(^|_)y$/i) ?? measures[1],
               x2: byName(/(^|_)end_x$/i) ?? measures[2],
               y2: byName(/(^|_)end_y$/i) ?? measures[3] }
    default:
      return {}
  }
}

export function idx(cols: Column[], name?: string): number {
  return name ? cols.findIndex((c) => c.name === name) : -1
}
export function num(v: Cell): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) return v
  if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) return Number(v)
  return null
}
export function str(v: Cell): string {
  if (v === null || v === undefined) return '∅'
  if (typeof v === 'object') return JSON.stringify(v)
  return String(v)
}
export function fmt(v: Cell): string {
  if (typeof v === 'number') {
    if (Number.isInteger(v)) return v.toLocaleString()
    return Math.abs(v) >= 100 ? v.toFixed(1) : v.toFixed(3).replace(/0+$/, '').replace(/\.$/, '')
  }
  return str(v)
}

/** Series key -> color, fixed order of first appearance; the 7th+ share "other". */
export function seriesColors(keys: string[]): Map<string, string> {
  const p = palette()
  const m = new Map<string, string>()
  keys.forEach((k, i) => m.set(k, i < p.length ? p[i]! : otherColor()))
  return m
}

// ── shared bits ────────────────────────────────────────────────────────────

type Tip = { x: number; y: number; lines: string[] } | null

function Tooltip({ tip }: { tip: Tip }) {
  if (!tip) return null
  return (
    <div className="pg-tip" style={{ left: tip.x + 12, top: tip.y + 12 }} role="status">
      {tip.lines.map((l, i) => <div key={i}>{l}</div>)}
    </div>
  )
}

function Legend({ colors }: { colors: Map<string, string> }) {
  if (colors.size < 2) return null
  return (
    <ul className="pg-legend" aria-label="Series">
      {[...colors.entries()].map(([k, c]) => (
        <li key={k}><span className="pg-swatch" style={{ background: c }} />{k}</li>
      ))}
    </ul>
  )
}

function Empty({ why }: { why: string }) {
  return <p className="pg-empty">{why}</p>
}

function useTip() {
  const [tip, setTip] = useState<Tip>(null)
  const show = (e: React.MouseEvent, lines: string[]) => {
    const host = (e.currentTarget as SVGElement).closest('.pg-chart') as HTMLElement | null
    const r = host?.getBoundingClientRect()
    setTip({ x: e.clientX - (r?.left ?? 0), y: e.clientY - (r?.top ?? 0), lines })
  }
  return { tip, show, hide: () => setTip(null) }
}

const W = 720
const H = 360
const PAD = { l: 56, r: 16, t: 16, b: 44 }

function scale(min: number, max: number, a: number, b: number) {
  const span = max - min || 1
  return (v: number) => a + ((v - min) / span) * (b - a)
}
function extent(vals: number[]): [number, number] {
  let lo = Infinity, hi = -Infinity
  for (const v of vals) { if (v < lo) lo = v; if (v > hi) hi = v }
  if (!Number.isFinite(lo)) return [0, 1]
  if (lo === hi) return [lo - 1, hi + 1]
  return [lo, hi]
}
function ticks(lo: number, hi: number, n = 5): number[] {
  const step = (hi - lo) / n
  const mag = Math.pow(10, Math.floor(Math.log10(step || 1)))
  const nice = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= step) ?? mag
  const out: number[] = []
  for (let v = Math.ceil(lo / nice) * nice; v <= hi + 1e-9; v += nice) out.push(+v.toFixed(10))
  return out
}

/** Axis titles — the mapped column names, so the chart says what it plots
 *  without the mapping panel open. x along the bottom edge, y rotated up
 *  the left edge. */
function AxisTitles({ x, y, w = W, padB = PAD.b }: {
  x?: string; y?: string; w?: number; padB?: number
}) {
  return (
    <>
      {x && <text x={(PAD.l + w - PAD.r) / 2} y={H - 4} className="pg-axis pg-axis-title"
                  textAnchor="middle">{x}</text>}
      {y && <text transform={`rotate(-90 11 ${(PAD.t + H - padB) / 2})`} x={11}
                  y={(PAD.t + H - padB) / 2} className="pg-axis pg-axis-title"
                  textAnchor="middle">{y}</text>}
    </>
  )
}

// ── table ──────────────────────────────────────────────────────────────────

/** The column whose frame a data table's video link opens, or -1: a
 *  moment's frame, else where it starts, else where it ends — in that
 *  order whatever the column order, so a possession keeping start_frame
 *  beside frame never links to its end (release review of 60eab2ae). */
export const FRAME_COL_NAMES = ['frame', 'start_frame', 'end_frame']
export function frameColumnIndex(columns: Column[]): number {
  for (const name of FRAME_COL_NAMES) {
    const i = columns.findIndex((c) => c.name === name)
    if (i >= 0) return i
  }
  return -1
}

export function DataTable({ result, max = 200, frameColIdx = -1, onFrame }: {
  result: Result; max?: number
  /** With both set, that column's cells become "open the match here"
   *  links: the host seeks its video to the row's tracking frame. */
  frameColIdx?: number; onFrame?: (frame: number) => void
}) {
  const rows = result.rows.slice(0, max)
  const linkable = onFrame && frameColIdx >= 0
  return (
    <div className="pg-table-wrap">
      <table className="pg-table">
        <thead>
          <tr>{result.columns.map((c) => (
            <th key={c.name} className={isMeasure(c) ? 'num' : ''} title={c.type}>{c.name}</th>
          ))}</tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>{r.map((v, j) => {
              const frame = linkable && j === frameColIdx ? num(v) : null
              return (
                <td key={j} className={isMeasure(result.columns[j]!) ? 'num' : ''}>
                  {frame !== null ? (
                    <button type="button" className="pg-frame-link"
                            title="Open the match video at this moment"
                            onClick={() => onFrame!(frame)}>{fmt(v)}</button>
                  ) : fmt(v)}
                </td>
              )
            })}</tr>
          ))}
        </tbody>
      </table>
      {result.rows.length > max && (
        <p className="pg-note">Showing {max} of {result.rows.length.toLocaleString()} rows.</p>
      )}
    </div>
  )
}

// ── bar ────────────────────────────────────────────────────────────────────

/** Hard ceiling, for the SVG's sake, with a note when it bites — the old
 *  silent 60 cut off everything after bar 60 with no way to reach it. */
const MAX_BARS = 400
/** Narrowest readable bar; past the card's width the chart grows and the
 *  wrapper scrolls, so every bar stays reachable. */
const MIN_BW = 14

export function BarChart({ result, mapping }: { result: Result; mapping: Mapping }) {
  const xi = idx(result.columns, mapping.x), yi = idx(result.columns, mapping.y)
  const { tip, show, hide } = useTip()
  if (xi < 0 || yi < 0) return <Empty why="Pick a category (x) and a measure (y)." />
  const data = result.rows
    .map((r) => ({ k: str(r[xi]!), v: num(r[yi]!) }))
    .filter((d): d is { k: string; v: number } => d.v !== null)
    .slice(0, MAX_BARS)
  if (!data.length) return <Empty why="No numeric values in the measure column." />
  const [, hi] = extent(data.map((d) => Math.max(0, d.v)))
  const lo = Math.min(0, ...data.map((d) => d.v))
  // Rotated category labels once horizontal ones stop fitting; they need a
  // deeper bottom margin. ≤24 keeps the old horizontal labels.
  const rotated = data.length > 24
  const padB = rotated ? 78 : PAD.b
  const sy = scale(lo, hi, H - padB, PAD.t)
  const w = Math.max(W, PAD.l + PAD.r + data.length * MIN_BW)
  const scrolled = w > W
  const bw = (w - PAD.l - PAD.r) / data.length
  const showValues = mapping.values === 'show'
  const color = palette()[0]!
  const zero = sy(0)
  const trunc = (k: string, n: number) => (k.length > n ? k.slice(0, n - 1) + '…' : k)
  return (
    <div className="pg-chart">
      <div className={scrolled ? 'pg-chart-scroll' : undefined}>
        <svg viewBox={`0 0 ${w} ${H}`} role="img" aria-label={`${mapping.y} by ${mapping.x}`}
             style={scrolled ? { width: w, height: H, maxWidth: 'none' } : undefined}>
          {ticks(lo, hi).map((t) => (
            <g key={t}>
              <line x1={PAD.l} x2={w - PAD.r} y1={sy(t)} y2={sy(t)} className="pg-grid" />
              <text x={PAD.l - 8} y={sy(t)} className="pg-axis" textAnchor="end" dy="0.32em">{fmt(t)}</text>
            </g>
          ))}
          {data.map((d, i) => {
            const x = PAD.l + i * bw + 1
            const top = Math.min(sy(d.v), zero), h = Math.abs(sy(d.v) - zero)
            return (
              <g key={i} onMouseMove={(e) => show(e, [d.k, `${mapping.y}: ${fmt(d.v)}`])} onMouseLeave={hide}>
                <rect x={x} y={top} width={Math.max(1, bw - 2)} height={Math.max(1, h)} rx={2} fill={color} />
                <rect x={x} y={PAD.t} width={Math.max(1, bw - 2)} height={H - PAD.t - padB} fill="transparent" />
              </g>
            )
          })}
          <line x1={PAD.l} x2={w - PAD.r} y1={zero} y2={zero} className="pg-baseline" />
          {showValues && data.map((d, i) => {
            const cx = PAD.l + i * bw + bw / 2
            const yv = d.v >= 0 ? Math.min(sy(d.v), zero) - 4 : Math.max(sy(d.v), zero) + 12
            return bw >= 26
              ? <text key={i} x={cx} y={yv} className="pg-axis pg-value" textAnchor="middle">{fmt(d.v)}</text>
              : <text key={i} transform={`rotate(-90 ${cx} ${yv})`} x={cx} y={yv} dy="0.32em"
                      className="pg-axis pg-value" textAnchor={d.v >= 0 ? 'start' : 'end'}>{fmt(d.v)}</text>
          })}
          {rotated
            ? data.map((d, i) => {
                const cx = PAD.l + i * bw + bw / 2
                return (
                  <text key={i} transform={`rotate(-45 ${cx} ${H - padB + 12})`} x={cx} y={H - padB + 12}
                        className="pg-axis" textAnchor="end">{trunc(d.k, 14)}</text>
                )
              })
            : data.map((d, i) => (
              <text key={i} x={PAD.l + i * bw + bw / 2} y={H - padB + 16} className="pg-axis"
                    textAnchor="middle">{trunc(d.k, 10)}</text>
            ))}
          <AxisTitles x={mapping.x} y={mapping.y} w={w} padB={padB} />
        </svg>
      </div>
      {result.rows.length > MAX_BARS && (
        <p className="pg-note">Showing the first {MAX_BARS} of {result.rows.length.toLocaleString()} bars — filter or aggregate to see the rest.</p>
      )}
      <Tooltip tip={tip} />
    </div>
  )
}

// ── line ───────────────────────────────────────────────────────────────────

export function LineChart({ result, mapping }: { result: Result; mapping: Mapping }) {
  const xi = idx(result.columns, mapping.x), yi = idx(result.columns, mapping.y)
  const si = idx(result.columns, mapping.series)
  const { tip, show, hide } = useTip()
  if (xi < 0 || yi < 0) return <Empty why="Pick an ordered x and a measure (y)." />
  const groups = new Map<string, { x: number; y: number }[]>()
  for (const r of result.rows) {
    const x = num(r[xi]!), y = num(r[yi]!)
    if (x === null || y === null) continue
    const k = si >= 0 ? str(r[si]!) : mapping.y!
    if (!groups.has(k)) groups.set(k, [])
    groups.get(k)!.push({ x, y })
  }
  if (!groups.size) return <Empty why="No numeric x/y pairs." />
  const all = [...groups.values()].flat()
  const [xlo, xhi] = extent(all.map((p) => p.x)), [ylo, yhi] = extent(all.map((p) => p.y))
  const sx = scale(xlo, xhi, PAD.l, W - PAD.r), sy = scale(ylo, yhi, H - PAD.b, PAD.t)
  const colors = seriesColors([...groups.keys()])
  return (
    <div className="pg-chart">
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`${mapping.y} over ${mapping.x}`}>
        {ticks(ylo, yhi).map((t) => (
          <g key={t}>
            <line x1={PAD.l} x2={W - PAD.r} y1={sy(t)} y2={sy(t)} className="pg-grid" />
            <text x={PAD.l - 8} y={sy(t)} className="pg-axis" textAnchor="end" dy="0.32em">{fmt(t)}</text>
          </g>
        ))}
        {ticks(xlo, xhi, 6).map((t) => (
          <text key={t} x={sx(t)} y={H - PAD.b + 16} className="pg-axis" textAnchor="middle">{fmt(t)}</text>
        ))}
        {[...groups.entries()].map(([k, pts]) => {
          const sorted = [...pts].sort((a, b) => a.x - b.x)
          const d = sorted.map((p, i) => `${i ? 'L' : 'M'}${sx(p.x).toFixed(1)},${sy(p.y).toFixed(1)}`).join(' ')
          return (
            <g key={k}>
              <path d={d} fill="none" stroke={colors.get(k)} strokeWidth={2} strokeLinejoin="round" />
              {sorted.length <= 400 && sorted.map((p, i) => (
                <circle key={i} cx={sx(p.x)} cy={sy(p.y)} r={6} fill="transparent"
                        onMouseMove={(e) => show(e, [k, `${mapping.x}: ${fmt(p.x)}`, `${mapping.y}: ${fmt(p.y)}`])}
                        onMouseLeave={hide} />
              ))}
            </g>
          )
        })}
        <AxisTitles x={mapping.x} y={mapping.y} />
      </svg>
      <Legend colors={colors} />
      <Tooltip tip={tip} />
    </div>
  )
}

// ── scatter ────────────────────────────────────────────────────────────────

export function ScatterChart({ result, mapping }: { result: Result; mapping: Mapping }) {
  const xi = idx(result.columns, mapping.x), yi = idx(result.columns, mapping.y)
  const si = idx(result.columns, mapping.series), zi = idx(result.columns, mapping.size)
  const { tip, show, hide } = useTip()
  if (xi < 0 || yi < 0) return <Empty why="Pick two measures (x and y)." />
  const pts = result.rows.flatMap((r) => {
    const x = num(r[xi]!), y = num(r[yi]!)
    if (x === null || y === null) return []
    return [{ x, y, k: si >= 0 ? str(r[si]!) : '', z: zi >= 0 ? num(r[zi]!) : null }]
  })
  if (!pts.length) return <Empty why="No numeric x/y pairs." />
  const [xlo, xhi] = extent(pts.map((p) => p.x)), [ylo, yhi] = extent(pts.map((p) => p.y))
  const sx = scale(xlo, xhi, PAD.l, W - PAD.r), sy = scale(ylo, yhi, H - PAD.b, PAD.t)
  const keys = [...new Set(pts.map((p) => p.k))]
  const colors = seriesColors(keys)
  const zs = pts.map((p) => p.z).filter((z): z is number => z !== null)
  const [zlo, zhi] = extent(zs.length ? zs : [1])
  const sr = scale(zlo, zhi, 3, 14)
  return (
    <div className="pg-chart">
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`${mapping.y} against ${mapping.x}`}>
        {ticks(ylo, yhi).map((t) => (
          <g key={t}>
            <line x1={PAD.l} x2={W - PAD.r} y1={sy(t)} y2={sy(t)} className="pg-grid" />
            <text x={PAD.l - 8} y={sy(t)} className="pg-axis" textAnchor="end" dy="0.32em">{fmt(t)}</text>
          </g>
        ))}
        {ticks(xlo, xhi, 6).map((t) => (
          <text key={t} x={sx(t)} y={H - PAD.b + 16} className="pg-axis" textAnchor="middle">{fmt(t)}</text>
        ))}
        {pts.slice(0, 3000).map((p, i) => (
          <circle key={i} cx={sx(p.x)} cy={sy(p.y)} r={p.z !== null ? sr(p.z) : 4}
                  fill={colors.get(p.k)} fillOpacity={0.8} className="pg-dot"
                  onMouseMove={(e) => show(e, [p.k, `${mapping.x}: ${fmt(p.x)}`, `${mapping.y}: ${fmt(p.y)}`,
                    ...(p.z !== null ? [`${mapping.size}: ${fmt(p.z)}`] : [])].filter(Boolean))}
                  onMouseLeave={hide} />
        ))}
        <AxisTitles x={mapping.x} y={mapping.y} />
      </svg>
      <Legend colors={colors} />
      <Tooltip tip={tip} />
    </div>
  )
}

// ── pitch ──────────────────────────────────────────────────────────────────

export type PitchOptions = {
  lengthM: number
  widthM: number
  /** 'center' = origin at the centre spot; 'corner' = origin at the
   *  bottom-left corner (the layer stack's frame). +y is up in both. */
  origin: 'center' | 'corner'
  /** Divide incoming coordinates by this (100 for centimetres). */
  unitsDivisor: number
  /** Optional clip region: show only this slice of the pitch, zoomed.
   *  x0/x1 in metres from the left goal line; y0/y1 from the bottom. */
  clip?: { x0: number; x1: number; y0: number; y1: number }
  /** Attacking-direction captions, by CLUB — "SKC attacking" toward the
   *  right goal, "LAG attacking" toward the left. Set only when the rows
   *  were actually oriented to the house rule (ours -> right); an
   *  unoriented pitch stays uncaptioned rather than claiming a direction
   *  it does not have (Ben, 2026-09-03). */
  attack?: { rightward: string; leftward?: string }
}

/** Region labels (from the engine's region hint) mapped to clip rects.
 *  Coordinates are in metres from the left goal line / bottom touchline. */
export function regionClip(region: string): PitchOptions['clip'] | undefined {
  const L = DEFAULT_LENGTH_M, W = 68
  const [low, mid, high] = edgesFromGoalLineM(L)
  switch (region) {
    case 'attacking third':   return { x0: high, x1: L, y0: 0, y1: W }
    case 'defensive third':   return { x0: 0, x1: low, y0: 0, y1: W }
    case 'middle third':      return { x0: low, x1: high, y0: 0, y1: W }
    case 'attacking half':    return { x0: mid, x1: L, y0: 0, y1: W }
    case 'defensive half':    return { x0: 0, x1: mid, y0: 0, y1: W }
    default: return undefined
  }
}

export const DEFAULT_PITCH: PitchOptions = { lengthM: 105, widthM: 68, origin: 'center', unitsDivisor: 1 }

/** Shared pitch geometry: view box, metre-to-pixel transform, and the
 *  metre-space normalization (origin handling + units divisor).
 *
 *  When `pitch.clip` is set the view is zoomed to that metre-space rect:
 *  coordinates still map into the full pitch, but the SVG viewBox crops to
 *  the clip, so the chart shows only that region at full resolution. */
export function pitchGeom(pitch: PitchOptions) {
  const L = pitch.lengthM, Wd = pitch.widthM
  const S = 6, P = 14
  /** Row coordinates -> metre coordinates with origin at the top-left corner. */
  const toMetres = (x: number, y: number) => {
    const xm = x / pitch.unitsDivisor, ym = y / pitch.unitsDivisor
    const cx = pitch.origin === 'center' ? xm + L / 2 : xm
    const cy = pitch.origin === 'center' ? Wd / 2 - ym : Wd - ym
    return [cx, cy] as const
  }
  const toPx = (x: number, y: number) => {
    const [cx, cy] = toMetres(x, y)
    return [P + cx * S, P + cy * S] as const
  }
  // Clipped viewBox when the question asked about a region.
  const c = pitch.clip
  const vw = c ? (c.x1 - c.x0) * S + 2 * P : L * S + 2 * P
  const vh = c ? (c.y1 - c.y0) * S + 2 * P : Wd * S + 2 * P
  const vx = c ? c.x0 * S : 0
  const vy = c ? c.y0 * S : 0
  /** The SVG viewBox string — clips to the region when set. */
  const viewBox = `${vx} ${vy} ${vw} ${vh}`
  return { L, Wd, S, P, vw, vh, viewBox, toMetres, toPx }
}

/** The pitch outline — box, halfway line, centre circle, penalty areas. */
export function PitchLines({ g }: { g: ReturnType<typeof pitchGeom> }) {
  const { L, Wd, S, P } = g
  const line = 'var(--color-border-strong)'
  const box = { d: 16.5 * S, h: 40.32 * S }, six = { d: 5.5 * S, h: 18.32 * S }
  const midX = P + (L * S) / 2, midY = P + (Wd * S) / 2
  return (
    <>
      <rect x={P} y={P} width={L * S} height={Wd * S} fill="none" stroke={line} strokeWidth={1.5} />
      <line x1={midX} x2={midX} y1={P} y2={P + Wd * S} stroke={line} strokeWidth={1.5} />
      <circle cx={midX} cy={midY} r={9.15 * S} fill="none" stroke={line} strokeWidth={1.5} />
      <rect x={P} y={midY - box.h / 2} width={box.d} height={box.h} fill="none" stroke={line} strokeWidth={1.5} />
      <rect x={P + L * S - box.d} y={midY - box.h / 2} width={box.d} height={box.h} fill="none" stroke={line} strokeWidth={1.5} />
      <rect x={P} y={midY - six.h / 2} width={six.d} height={six.h} fill="none" stroke={line} strokeWidth={1} />
      <rect x={P + L * S - six.d} y={midY - six.h / 2} width={six.d} height={six.h} fill="none" stroke={line} strokeWidth={1} />
    </>
  )
}

/** Club-named direction captions along the top edge: who attacks the right
 *  goal, who attacks the left. Rendered only when the pitch options carry
 *  them — see PitchOptions.attack. */
export function AttackCaption({ g, pitch }: { g: ReturnType<typeof pitchGeom>; pitch: PitchOptions }) {
  const a = pitch.attack
  if (!a) return null
  return (
    <>
      <text x={g.P + g.L * g.S - 4} y={g.P + 14} textAnchor="end"
            className="pg-shape-measure">{a.rightward} &#8594;</text>
      {a.leftward && (
        <text x={g.P + 4} y={g.P + 14} textAnchor="start"
              className="pg-shape-measure">&#8592; {a.leftward}</text>
      )}
    </>
  )
}

export function PitchMap({ result, mapping, pitch }: { result: Result; mapping: Mapping; pitch: PitchOptions }) {
  const xi = idx(result.columns, mapping.x), yi = idx(result.columns, mapping.y)
  const si = idx(result.columns, mapping.series), li = idx(result.columns, mapping.label)
  const zi = idx(result.columns, mapping.size)
  const { tip, show, hide } = useTip()
  if (xi < 0 || yi < 0) return <Empty why="Pick the x and y coordinate columns." />
  const g = pitchGeom(pitch)
  const pts = result.rows.flatMap((r) => {
    const x = num(r[xi]!), y = num(r[yi]!)
    if (x === null || y === null) return []
    return [{ x, y, k: si >= 0 ? str(r[si]!) : '', l: li >= 0 ? str(r[li]!) : '',
              z: zi >= 0 ? num(r[zi]!) : null }]
  })
  const keys = [...new Set(pts.map((p) => p.k))]
  const colors = seriesColors(keys)
  const zs = pts.map((p) => p.z).filter((z): z is number => z !== null)
  const [zlo, zhi] = extent(zs.length ? zs : [1])
  const sr = scale(zlo, zhi, 3, 12)
  return (
    <div className="pg-chart pg-pitch">
      <svg viewBox={g.viewBox} role="img" aria-label="Pitch map">
        <rect x={g.P} y={g.P} width={g.L * g.S} height={g.Wd * g.S}
              fill="var(--color-surface-sunken)" />
        <PitchLines g={g} />
        {pts.slice(0, 5000).map((p, i) => {
          const [px, py] = g.toPx(p.x, p.y)
          return (
            <g key={i} onMouseMove={(e) => show(e, [p.k, p.l,
                  `x ${fmt(p.x)}  y ${fmt(p.y)}`,
                  ...(p.z !== null ? [`${mapping.size}: ${fmt(p.z)}`] : [])].filter(Boolean))}
               onMouseLeave={hide}>
              <circle cx={px} cy={py} r={p.z !== null ? sr(p.z) : 5}
                      fill={colors.get(p.k)} fillOpacity={0.85} className="pg-dot" />
              {pts.length <= 60 && p.l && (
                <text x={px} y={py - 8} className="pg-axis" textAnchor="middle">{p.l}</text>
              )}
            </g>
          )
        })}
        <AttackCaption g={g} pitch={pitch} />
      </svg>
      {!pts.length && <Empty why="No numeric coordinate pairs in the chosen columns." />}
      <Legend colors={colors} />
      <Tooltip tip={tip} />
    </div>
  )
}

// ── heat map ───────────────────────────────────────────────────────────────

/** Binned point density over the pitch. Single hue, opacity carries the
 *  count — theme-safe on both surfaces without a colour ramp. */
export function HeatMapChart({ result, mapping, pitch }: { result: Result; mapping: Mapping; pitch: PitchOptions }) {
  const xi = idx(result.columns, mapping.x), yi = idx(result.columns, mapping.y)
  if (xi < 0 || yi < 0) return <Empty why="Pick the x and y coordinate columns." />
  const g = pitchGeom(pitch)

  // When a region clip is active, bin WITHIN the clipped area so the cells
  // fill the zoomed view. Otherwise bins span the full pitch.
  const c = pitch.clip
  const bx0 = c ? c.x0 : 0, bx1 = c ? c.x1 : g.L
  const by0 = c ? c.y0 : 0, by1 = c ? c.y1 : g.Wd
  const bw = bx1 - bx0, bh = by1 - by0
  const COLS = c ? Math.round(28 * (bw / g.L)) : 28
  const ROWS = c ? Math.round(18 * (bh / g.Wd)) : 18
  const cw = bw / COLS, ch = bh / ROWS

  // Collect all points (for possible dot-map fallback).
  const pts: { px: number; py: number }[] = []
  const bins = new Array(COLS * ROWS).fill(0) as number[]
  let n = 0
  for (const r of result.rows) {
    const x = num(r[xi]!), y = num(r[yi]!)
    if (x === null || y === null) continue
    const [cx, cy] = g.toMetres(x, y)
    if (cx < bx0 || cx >= bx1 || cy < by0 || cy >= by1) continue
    const col = Math.min(COLS - 1, Math.floor((cx - bx0) / cw))
    const row = Math.min(ROWS - 1, Math.floor((cy - by0) / ch))
    bins[row * COLS + col] = (bins[row * COLS + col] ?? 0) + 1
    pts.push({ px: g.P + cx * g.S, py: g.P + cy * g.S })
    n += 1
  }
  if (!n) return <Empty why="No coordinate pairs land on the pitch." />

  // Few points: show dots instead of a heat map (a single point per cell
  // produces a uniform sheet rather than a density pattern).
  const color = palette()[0]!
  if (n <= 40) {
    return (
      <div className="pg-chart pg-pitch">
        <svg viewBox={g.viewBox} role="img" aria-label="Positions on the pitch">
          <rect x={g.P} y={g.P} width={g.L * g.S} height={g.Wd * g.S}
                fill="var(--color-surface-sunken)" />
          <PitchLines g={g} />
          {pts.map((p, i) => (
            <circle key={i} cx={p.px} cy={p.py} r={5}
                    fill={color} fillOpacity={0.75} />
          ))}
        </svg>
        <p className="pg-note">{n.toLocaleString()} points shown as dots (too few for a density pattern).</p>
      </div>
    )
  }

  const max = Math.max(...bins)
  return (
    <div className="pg-chart pg-pitch">
      <svg viewBox={g.viewBox} role="img" aria-label="Pitch density heat map">
        <rect x={g.P} y={g.P} width={g.L * g.S} height={g.Wd * g.S}
              fill="var(--color-surface-sunken)" />
        {bins.map((cnt, i) => {
          if (!cnt) return null
          const col = i % COLS, row = Math.floor(i / COLS)
          return (
            <rect key={i}
                  x={g.P + (bx0 + col * cw) * g.S} y={g.P + (by0 + row * ch) * g.S}
                  width={cw * g.S} height={ch * g.S}
                  fill={color} fillOpacity={0.08 + 0.82 * (cnt / max)}>
              <title>{cnt.toLocaleString()} of {n.toLocaleString()}</title>
            </rect>
          )
        })}
        <PitchLines g={g} />
        <AttackCaption g={g} pitch={pitch} />
      </svg>
      <p className="pg-note">{n.toLocaleString()} points; darkest cell holds {max.toLocaleString()}.</p>
    </div>
  )
}

// ── flow (start-to-end traces) ─────────────────────────────────────────────

export function FlowMap({ result, mapping, pitch }: { result: Result; mapping: Mapping; pitch: PitchOptions }) {
  const xi = idx(result.columns, mapping.x), yi = idx(result.columns, mapping.y)
  const x2i = idx(result.columns, mapping.x2), y2i = idx(result.columns, mapping.y2)
  const si = idx(result.columns, mapping.series)
  const { tip, show, hide } = useTip()
  if (xi < 0 || yi < 0 || x2i < 0 || y2i < 0) {
    return <Empty why="Pick start (x, y) and end (x2, y2) coordinate columns." />
  }
  const g = pitchGeom(pitch)
  const segs = result.rows.flatMap((r) => {
    const x = num(r[xi]!), y = num(r[yi]!), x2 = num(r[x2i]!), y2 = num(r[y2i]!)
    if (x === null || y === null || x2 === null || y2 === null) return []
    return [{ x, y, x2, y2, k: si >= 0 ? str(r[si]!) : '' }]
  })
  if (!segs.length) return <Empty why="No complete start/end pairs in the chosen columns." />
  const colors = seriesColors([...new Set(segs.map((s) => s.k))])
  return (
    <div className="pg-chart pg-pitch">
      <svg viewBox={g.viewBox} role="img" aria-label="Ball path traces">
        <rect x={g.P} y={g.P} width={g.L * g.S} height={g.Wd * g.S}
              fill="var(--color-surface-sunken)" />
        <PitchLines g={g} />
        {segs.slice(0, 1500).map((s, i) => {
          const [ax, ay] = g.toPx(s.x, s.y)
          const [bx, by] = g.toPx(s.x2, s.y2)
          const c = colors.get(s.k)
          return (
            <g key={i}
               onMouseMove={(e) => show(e, [s.k,
                 `from ${fmt(s.x)}, ${fmt(s.y)}`,
                 `to ${fmt(s.x2)}, ${fmt(s.y2)}`].filter(Boolean))}
               onMouseLeave={hide}>
              <line x1={ax} y1={ay} x2={bx} y2={by}
                    stroke={c} strokeWidth={1.5} strokeOpacity={0.55} />
              <circle cx={bx} cy={by} r={3} fill={c} fillOpacity={0.9} className="pg-dot" />
            </g>
          )
        })}
        <AttackCaption g={g} pitch={pitch} />
      </svg>
      {segs.length > 1500 && (
        <p className="pg-note">Showing 1,500 of {segs.length.toLocaleString()} traces.</p>
      )}
      <Legend colors={colors} />
      <Tooltip tip={tip} />
    </div>
  )
}

// ── histogram ──────────────────────────────────────────────────────────────

export function HistogramChart({ result, mapping }: { result: Result; mapping: Mapping }) {
  const xi = idx(result.columns, mapping.x)
  const { tip, show, hide } = useTip()
  if (xi < 0) return <Empty why="Pick the measure to bin." />
  const vals = result.rows
    .map((r) => num(r[xi]!))
    .filter((v): v is number => v !== null)
  if (!vals.length) return <Empty why="No numeric values in the chosen column." />
  const [lo, hi] = extent(vals)
  const nBins = Math.min(30, Math.max(8, Math.ceil(Math.sqrt(vals.length))))
  const bw = (hi - lo) / nBins || 1
  const counts = new Array(nBins).fill(0) as number[]
  for (const v of vals) {
    const ci = Math.min(nBins - 1, Math.floor((v - lo) / bw))
    counts[ci] = (counts[ci] ?? 0) + 1
  }
  const cmax = Math.max(...counts)
  const sy = scale(0, cmax, H - PAD.b, PAD.t)
  const sx = scale(lo, hi, PAD.l, W - PAD.r)
  const color = palette()[0]!
  return (
    <div className="pg-chart">
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`Distribution of ${mapping.x}`}>
        {ticks(0, cmax).map((t) => (
          <g key={t}>
            <line x1={PAD.l} x2={W - PAD.r} y1={sy(t)} y2={sy(t)} className="pg-grid" />
            <text x={PAD.l - 8} y={sy(t)} className="pg-axis" textAnchor="end" dy="0.32em">{fmt(t)}</text>
          </g>
        ))}
        {ticks(lo, hi, 6).map((t) => (
          <text key={t} x={sx(t)} y={H - PAD.b + 16} className="pg-axis" textAnchor="middle">{fmt(t)}</text>
        ))}
        {counts.map((c, i) => {
          const bx = sx(lo + i * bw)
          const bx2 = sx(lo + (i + 1) * bw)
          return (
            <rect key={i} x={bx + 0.5} y={sy(c)}
                  width={Math.max(1, bx2 - bx - 1)} height={Math.max(0, sy(0) - sy(c))}
                  fill={color} fillOpacity={0.85}
                  onMouseMove={(e) => show(e, [
                    `${fmt(lo + i * bw)} \u2013 ${fmt(lo + (i + 1) * bw)}`,
                    `${c.toLocaleString()} rows`])}
                  onMouseLeave={hide} />
          )
        })}
        <line x1={PAD.l} x2={W - PAD.r} y1={sy(0)} y2={sy(0)} className="pg-baseline" />
      </svg>
      <p className="pg-note">
        {vals.length.toLocaleString()} values in {nBins} bins.
      </p>
      <Tooltip tip={tip} />
    </div>
  )
}

// ── pitch zones (categorical regions, no coordinates) ──────────────────────

/** Region-label -> rect in top-left metre space [x0, x1, y0, y1], on the
 *  NL engine's lines (lib/pitchZones: zones on 105 m, wings beyond the
 *  penalty area's edges), so its labels land here without translation. The
 *  drawn pitch attacks right, so the left wing is the top band. `wing (+y)`
 *  and `wing (-y)` come only from a saved answer or saved SQL, cut on raw y
 *  11.3 m either side; zoneRectsFor draws them. */
export function zoneRects(L: number, Wd: number): Record<string, [number, number, number, number]> {
  const [low, , high] = edgesM(DEFAULT_LENGTH_M)
  const b = L / 2 + low, c = L / 2, d = L / 2 + high
  const pl = Wd / 2 - PEN_AREA_HALF_WIDTH_M, pr = Wd / 2 + PEN_AREA_HALF_WIDTH_M
  const wl = Wd / 2 - 11.3, wr = Wd / 2 + 11.3
  const full: [number, number] = [0, Wd]
  return {
    'def': [0, b, ...full], 'mfd': [b, c, ...full],
    'mfa': [c, d, ...full], 'att': [d, L, ...full],
    'defensive third': [0, b, ...full], 'own third': [0, b, ...full],
    'middle third': [b, d, ...full], 'mid third': [b, d, ...full],
    'attacking third': [d, L, ...full], 'final third': [d, L, ...full],
    'defensive half': [0, c, ...full], 'own half': [0, c, ...full],
    'attacking half': [c, L, ...full],
    'left wing': [0, L, 0, pl],
    'central': [0, L, pl, pr],
    'right wing': [0, L, pr, Wd],
    'wing (+y)': [0, L, 0, wl],
    'wing (-y)': [0, L, wr, Wd],
  }
}

/** The rects one answer's labels draw on. An answer with a `wing (+y)` or
 *  `wing (-y)` label is cut 11.3 m either side of the long axis, so its
 *  `central` is the band between those two wings. */
export function zoneRectsFor(
  L: number, Wd: number, labels: string[],
): Record<string, [number, number, number, number]> {
  const rects = zoneRects(L, Wd)
  const keys = new Set(labels.map((l) => l.trim().toLowerCase().replace(/_/g, ' ')))
  if (keys.has('wing (+y)') || keys.has('wing (-y)')) {
    rects['central'] = [0, L, rects['wing (+y)']![3], rects['wing (-y)']![2]]
  }
  return rects
}

/** Categorical spatial results — "which zones", "which flank" — shaded
 *  straight onto the pitch. The region NAME is the location; no
 *  coordinates required. Unrecognized labels are listed, never guessed. */
export function PitchZonesChart({ result, mapping, pitch, frameColIdx, onFrame }: {
  result: Result; mapping: Mapping; pitch: PitchOptions
  frameColIdx?: number; onFrame?: (frame: number) => void }) {
  const xi = idx(result.columns, mapping.x)
  const yi = idx(result.columns, mapping.y)
  if (xi < 0) return <Empty why="Pick the region column (zone, channel, segment)." />
  const g = pitchGeom(pitch)
  const rects = zoneRectsFor(g.L, g.Wd, result.rows.map((r) => str(r[xi]!)))

  const matched: { label: string; rect: [number, number, number, number]; val: number | null }[] = []
  const unmatched: string[] = []
  for (const r of result.rows) {
    const label = str(r[xi]!).trim()
    // Normalize underscores: the parquets write "attacking_third" while
    // the vocabulary keys are "attacking third".
    const rect = rects[label.toLowerCase().replace(/_/g, ' ')]
    if (!rect) { unmatched.push(label); continue }
    matched.push({ label, rect, val: yi >= 0 ? num(r[yi]!) : null })
  }
  if (!matched.length) {
    // The engine hinted pitchzones but the values don't match the
    // vocabulary — fall back to the data table rather than an error.
    return <DataTable result={result} frameColIdx={frameColIdx} onFrame={onFrame} />
  }
  const vals = matched.map((m) => m.val).filter((v): v is number => v !== null)
  const [lo, hi] = extent(vals.length ? vals : [1])
  const alpha = (v: number | null) =>
    v === null || hi === lo ? 0.45 : 0.12 + 0.72 * ((v - lo) / (hi - lo))
  const color = palette()[0]!

  return (
    <div className="pg-chart pg-pitch">
      <svg viewBox={g.viewBox} role="img" aria-label="Regions shaded on the pitch">
        <rect x={g.P} y={g.P} width={g.L * g.S} height={g.Wd * g.S}
              fill="var(--color-surface-sunken)" />
        {matched.map((m, i) => {
          const [x0, x1, y0, y1] = m.rect
          return (
            <rect key={i}
                  x={g.P + x0 * g.S} y={g.P + y0 * g.S}
                  width={(x1 - x0) * g.S} height={(y1 - y0) * g.S}
                  fill={color} fillOpacity={alpha(m.val)} />
          )
        })}
        <PitchLines g={g} />
        {matched.map((m, i) => {
          const [x0, x1, y0, y1] = m.rect
          const cx = g.P + ((x0 + x1) / 2) * g.S
          const cy = g.P + ((y0 + y1) / 2) * g.S
          return (
            <g key={`t${i}`}>
              <text x={cx} y={cy - 6} textAnchor="middle" className="pg-zone-label">
                {m.label}
              </text>
              {m.val !== null && (
                <text x={cx} y={cy + 12} textAnchor="middle" className="pg-zone-value">
                  {fmt(m.val)}
                </text>
              )}
            </g>
          )
        })}
      </svg>
      {unmatched.length > 0 && (
        <p className="pg-note">
          Not spatial, shown in the data table only: {unmatched.join(', ')}
        </p>
      )}
    </div>
  )
}

// ── shape, space, network and rose (all left-to-right, like every pitch) ──

/** Convex hull (monotone chain). Exported for its test. */
export function convexHull(pts: [number, number][]): [number, number][] {
  if (pts.length < 3) return pts.slice()
  const s = pts.slice().sort((a, b) => a[0] - b[0] || a[1] - b[1])
  const cross = (o: [number, number], a: [number, number], b: [number, number]) =>
    (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0])
  const lower: [number, number][] = []
  for (const p of s) {
    while (lower.length >= 2 && cross(lower[lower.length - 2]!, lower[lower.length - 1]!, p) <= 0) lower.pop()
    lower.push(p)
  }
  const upper: [number, number][] = []
  for (const p of s.slice().reverse()) {
    while (upper.length >= 2 && cross(upper[upper.length - 2]!, upper[upper.length - 1]!, p) <= 0) upper.pop()
    upper.push(p)
  }
  return lower.slice(0, -1).concat(upper.slice(0, -1))
}

/**
 * Team shape, left to right like every pitch here: one dot per row (a
 * player's average position), the convex hull of the shape, and the hull's
 * measured width and depth written on the pitch in metres. A facet column
 * splits into small multiples — one mini pitch per facet value.
 */
export function ShapeChart({ result, mapping, pitch }: { result: Result; mapping: Mapping; pitch: PitchOptions }) {
  const xi = idx(result.columns, mapping.x), yi = idx(result.columns, mapping.y)
  const li = idx(result.columns, mapping.label), fi = idx(result.columns, mapping.facet)
  if (xi < 0 || yi < 0) return <Empty why="Pick the x and y columns (average positions in metres)." />
  const g = pitchGeom(pitch)

  // Facet -> points, in first-appearance order.
  const facets = new Map<string, { m: [number, number]; px: [number, number]; label: string }[]>()
  for (const r of result.rows) {
    const x = num(r[xi]!), y = num(r[yi]!)
    if (x === null || y === null) continue
    const key = fi >= 0 ? str(r[fi]!) : ''
    if (!facets.has(key)) facets.set(key, [])
    facets.get(key)!.push({
      m: g.toMetres(x, y) as unknown as [number, number],
      px: g.toPx(x, y) as unknown as [number, number],
      label: li >= 0 ? str(r[li]!) : '',
    })
  }
  if (!facets.size) return <Empty why="No numeric positions in the result." />
  const color = palette()[0]!
  const multiple = facets.size > 1

  const one = (key: string, pts: { m: [number, number]; px: [number, number]; label: string }[]) => {
    const hull = convexHull(pts.map((p) => p.px))
    const hullM = convexHull(pts.map((p) => p.m))
    const xs = hullM.map((p) => p[0]), ys = hullM.map((p) => p[1])
    const depth = xs.length ? Math.max(...xs) - Math.min(...xs) : 0
    const width = ys.length ? Math.max(...ys) - Math.min(...ys) : 0
    const pxs = hull.map((p) => p[0]), pys = hull.map((p) => p[1])
    const left = Math.min(...pxs), right = Math.max(...pxs)
    const top = Math.min(...pys), bottom = Math.max(...pys)
    return (
      <div key={key} className={`pg-shape${multiple ? ' pg-shape--mini' : ''}`}>
        <svg viewBox={g.viewBox} role="img"
             aria-label={key ? `Average shape — ${key}` : 'Average shape'}>
          <rect x={g.P} y={g.P} width={g.L * g.S} height={g.Wd * g.S}
                fill="var(--color-surface-sunken)" />
          <PitchLines g={g} />
          {hull.length >= 3 && (
            <polygon points={hull.map((p) => p.join(',')).join(' ')}
                     fill={color} fillOpacity={0.14} stroke={color} strokeWidth={1.5} />
          )}
          {pts.length >= 2 && (
            <>
              {/* Depth ruler under the hull (the attack axis now runs
                  left-to-right), width ruler beside it — the compactness
                  numbers, written where they are measured. */}
              <line x1={left} x2={right} y1={bottom + 10} y2={bottom + 10}
                    stroke={color} strokeWidth={1} />
              <text x={(left + right) / 2} y={bottom + 22} textAnchor="middle"
                    className="pg-shape-measure">depth {depth.toFixed(1)}m</text>
              <line x1={right + 10} x2={right + 10} y1={top} y2={bottom}
                    stroke={color} strokeWidth={1} />
              <text x={right + 14} y={(top + bottom) / 2}
                    className="pg-shape-measure"
                    transform={`rotate(90 ${right + 14} ${(top + bottom) / 2})`}
                    textAnchor="middle">width {width.toFixed(1)}m</text>
            </>
          )}
          {pts.map((p, i) => (
            <g key={i}>
              <circle cx={p.px[0]} cy={p.px[1]} r={7} fill={color} fillOpacity={0.9} />
              {p.label && (
                <text x={p.px[0]} y={p.px[1] + 3} textAnchor="middle"
                      className="pg-shape-dot-label">{p.label}</text>
              )}
            </g>
          ))}
          <AttackCaption g={g} pitch={pitch} />
        </svg>
        {key && <p className="pg-shape-caption">{key}</p>}
      </div>
    )
  }

  return (
    <div className="pg-chart pg-pitch pg-shape-row">
      {[...facets.entries()].map(([key, pts]) => one(key, pts))}
    </div>
  )
}

/**
 * Space map: inverse occupancy, left to right. Cells a defending team
 * rarely stood in shine — between the lines, the weak-side wing, the space
 * a coach is looking for. Brighter = less occupied, and the note says so.
 */
export function SpaceMapChart({ result, mapping, pitch }: { result: Result; mapping: Mapping; pitch: PitchOptions }) {
  const xi = idx(result.columns, mapping.x), yi = idx(result.columns, mapping.y)
  if (xi < 0 || yi < 0) return <Empty why="Pick the x and y columns (positions in metres)." />
  const g = pitchGeom(pitch)
  const NA = 12, NL = 8 // bins along the attack axis / across the lateral axis
  const bins = new Array<number>(NA * NL).fill(0)
  let nPts = 0
  for (const r of result.rows) {
    const x = num(r[xi]!), y = num(r[yi]!)
    if (x === null || y === null) continue
    const [cx, cy] = g.toMetres(x, y)
    const a = Math.min(NA - 1, Math.max(0, Math.floor((cx / g.L) * NA)))
    const l = Math.min(NL - 1, Math.max(0, Math.floor((cy / g.Wd) * NL)))
    bins[a * NL + l] = (bins[a * NL + l] ?? 0) + 1
    nPts += 1
  }
  if (!nPts) return <Empty why="No numeric positions in the result." />
  const max = Math.max(...bins)
  const color = palette()[3]! // the amber slot — space, not a team
  const ca = (g.L * g.S) / NA, cl = (g.Wd * g.S) / NL
  return (
    <div className="pg-chart pg-pitch">
      <svg viewBox={g.viewBox} role="img"
           aria-label="Unoccupied space on the pitch">
        <rect x={g.P} y={g.P} width={g.L * g.S} height={g.Wd * g.S}
              fill="var(--color-surface-sunken)" />
        {bins.map((c, i) => {
          const a = Math.floor(i / NL), l = i % NL
          const alpha = max === 0 ? 0 : 0.62 * (1 - c / max)
          if (alpha <= 0.02) return null
          return (
            <rect key={i}
                  x={g.P + a * ca} y={g.P + l * cl}
                  width={ca} height={cl} fill={color} fillOpacity={alpha} />
          )
        })}
        <PitchLines g={g} />
        <AttackCaption g={g} pitch={pitch} />
      </svg>
      <p className="pg-note">Brighter cells were occupied least — the space left open.
        {' '}From {nPts.toLocaleString()} sampled positions.</p>
    </div>
  )
}

// ── timeline (categorical spans over the match clock) ──────────────────────

/**
 * When a state held, drawn as colored spans across the match clock —
 * "4-4-2 from 0 to 31, then 4-2-3-1 to 58". Rows are spans
 * (start minute, end minute, category, optional lane); categories share
 * the fixed palette and the legend names them.
 */
export function TimelineChart({ result, mapping }: { result: Result; mapping: Mapping }) {
  const si = idx(result.columns, mapping.x), ei = idx(result.columns, mapping.x2)
  const ci = idx(result.columns, mapping.label), gi = idx(result.columns, mapping.series)
  if (si < 0 || ei < 0 || ci < 0) {
    return <Empty why="Pick the start, end and category columns." />
  }
  type Span = { s: number; e: number; cat: string; lane: string }
  const spans: Span[] = []
  for (const r of result.rows) {
    const s = num(r[si]!), e = num(r[ei]!)
    if (s === null || e === null || e <= s) continue
    spans.push({ s, e, cat: str(r[ci]!), lane: gi >= 0 ? str(r[gi]!) : '' })
  }
  if (!spans.length) return <Empty why="No spans in the result." />

  const lanes = [...new Set(spans.map((x) => x.lane))]
  const cats = [...new Set(spans.map((x) => x.cat))]
  const colors = seriesColors(cats)
  const maxMin = Math.max(45, ...spans.map((x) => x.e))

  const W = 760, LANE_H = 34, PAD_L = 8, PAD_R = 8, AXIS_H = 22, PAD_T = 6
  const H = PAD_T + lanes.length * LANE_H + AXIS_H
  const px = (m: number) => PAD_L + (m / maxMin) * (W - PAD_L - PAD_R)
  const ticks: number[] = []
  for (let t = 0; t <= maxMin; t += 15) ticks.push(t)

  return (
    <div className="pg-chart pg-timeline">
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="State timeline">
        {lanes.map((lane, li) => {
          const y = PAD_T + li * LANE_H
          return (
            <g key={lane || 'all'}>
              <rect x={PAD_L} y={y + 4} width={W - PAD_L - PAD_R} height={LANE_H - 12}
                    fill="var(--color-surface-sunken)" />
              {spans.filter((x) => x.lane === lane).map((x, i) => {
                const w = px(x.e) - px(x.s)
                return (
                  <g key={i}>
                    <rect x={px(x.s)} y={y + 4} width={w} height={LANE_H - 12}
                          fill={colors.get(x.cat)} fillOpacity={0.85} />
                    {w > 46 && (
                      <text x={px(x.s) + w / 2} y={y + LANE_H / 2 + 2}
                            textAnchor="middle" className="pg-timeline__span">
                        {x.cat}
                      </text>
                    )}
                  </g>
                )
              })}
              {lane && (
                <text x={PAD_L} y={y + 1} className="pg-timeline__lane">{lane}</text>
              )}
            </g>
          )
        })}
        {ticks.map((t) => (
          <g key={t}>
            <line x1={px(t)} x2={px(t)} y1={PAD_T}
                  y2={PAD_T + lanes.length * LANE_H - 6}
                  stroke="var(--color-border)" strokeWidth={1} />
            <text x={px(t)} y={H - 6} textAnchor="middle"
                  className="pg-timeline__tick">{t}&prime;</text>
          </g>
        ))}
      </svg>
      <Legend colors={colors} />
    </div>
  )
}

// ── pass network (nodes at average positions, weighted edges) ──────────────

/**
 * Ball circulation, left to right: each player at their average position,
 * sized by involvement, connected by edges whose width is the pass count
 * between them. Rows are EDGES (from/to coordinates + count); the nodes
 * are derived from the edge endpoints.
 */
export function PassNetChart({ result, mapping, pitch }: { result: Result; mapping: Mapping; pitch: PitchOptions }) {
  const c = result.columns
  const fx = idx(c, mapping.x), fy = idx(c, mapping.y)
  const tx = idx(c, mapping.x2), ty = idx(c, mapping.y2)
  const fp = idx(c, mapping.label), tp = idx(c, mapping.series)
  const ni = idx(c, mapping.size)
  if (fx < 0 || fy < 0 || tx < 0 || ty < 0) {
    return <Empty why="Pick the from/to coordinate columns." />
  }
  const g = pitchGeom(pitch)
  type Edge = { a: [number, number]; b: [number, number]; n: number }
  const edges: Edge[] = []
  const nodes = new Map<string, { px: [number, number]; vol: number }>()
  const seat = (key: string, px: [number, number], n: number) => {
    const cur = nodes.get(key)
    if (cur) cur.vol += n
    else nodes.set(key, { px, vol: n })
  }
  for (const r of result.rows) {
    const x1 = num(r[fx]!), y1 = num(r[fy]!), x2 = num(r[tx]!), y2 = num(r[ty]!)
    if (x1 === null || y1 === null || x2 === null || y2 === null) continue
    const n = ni >= 0 ? (num(r[ni]!) ?? 1) : 1
    const a = g.toPx(x1, y1) as unknown as [number, number]
    const b = g.toPx(x2, y2) as unknown as [number, number]
    edges.push({ a, b, n })
    seat(fp >= 0 ? str(r[fp]!) : `${x1},${y1}`, a, n)
    seat(tp >= 0 ? str(r[tp]!) : `${x2},${y2}`, b, n)
  }
  if (!edges.length) return <Empty why="No edges in the result." />
  const maxN = Math.max(...edges.map((e) => e.n))
  const maxVol = Math.max(...[...nodes.values()].map((v) => v.vol))
  const color = palette()[0]!
  return (
    <div className="pg-chart pg-pitch pg-shape">
      <svg viewBox={g.viewBox} role="img" aria-label="Pass network">
        <rect x={g.P} y={g.P} width={g.L * g.S} height={g.Wd * g.S}
              fill="var(--color-surface-sunken)" />
        <PitchLines g={g} />
        {edges.map((e, i) => (
          <line key={i} x1={e.a[0]} y1={e.a[1]} x2={e.b[0]} y2={e.b[1]}
                stroke={color} strokeOpacity={0.55}
                strokeWidth={0.8 + 4.2 * (e.n / maxN)} />
        ))}
        {[...nodes.entries()].map(([key, v]) => (
          <g key={key}>
            <circle cx={v.px[0]} cy={v.px[1]} r={5 + 6 * (v.vol / maxVol)}
                    fill={color} fillOpacity={0.92} />
            <text x={v.px[0]} y={v.px[1] + 3} textAnchor="middle"
                  className="pg-shape-dot-label">{key}</text>
          </g>
        ))}
        <AttackCaption g={g} pitch={pitch} />
      </svg>
    </div>
  )
}

// ── direction rose (angular distribution of movements) ─────────────────────

/** Sector counts for the rose: 16 sectors, 0 = straight toward the
 *  attacked goal, clockwise. Exported for its test. */
export function roseSectors(
  segs: { dxAttack: number; dLat: number }[], sectors = 16,
): number[] {
  const out = new Array<number>(sectors).fill(0)
  for (const s of segs) {
    if (s.dxAttack === 0 && s.dLat === 0) continue
    // atan2(lateral, attack): 0 rad = straight up the pitch.
    let a = Math.atan2(s.dLat, s.dxAttack)
    if (a < 0) a += 2 * Math.PI
    const k = Math.min(sectors - 1, Math.floor((a / (2 * Math.PI)) * sectors))
    out[k] = (out[k] ?? 0) + 1
  }
  return out
}

/**
 * Where movements point: petals by direction, the attacked goal to the
 * RIGHT — the same way every pitch here reads. Rows are start/end
 * coordinates; the angle is computed in the attack frame.
 */
export function RoseChart({ result, mapping, pitch }: { result: Result; mapping: Mapping; pitch: PitchOptions }) {
  const c = result.columns
  const sx = idx(c, mapping.x), sy = idx(c, mapping.y)
  const ex = idx(c, mapping.x2), ey = idx(c, mapping.y2)
  if (sx < 0 || sy < 0 || ex < 0 || ey < 0) {
    return <Empty why="Pick the start/end coordinate columns." />
  }
  const g = pitchGeom(pitch)
  const segs: { dxAttack: number; dLat: number }[] = []
  for (const r of result.rows) {
    const x1 = num(r[sx]!), y1 = num(r[sy]!), x2 = num(r[ex]!), y2 = num(r[ey]!)
    if (x1 === null || y1 === null || x2 === null || y2 === null) continue
    const [a1, l1] = g.toMetres(x1, y1)
    const [a2, l2] = g.toMetres(x2, y2)
    segs.push({ dxAttack: a2 - a1, dLat: l2 - l1 })
  }
  if (!segs.length) return <Empty why="No segments in the result." />
  const SECTORS = 16
  const counts = roseSectors(segs, SECTORS)
  const max = Math.max(...counts)
  const W = 320, CX = W / 2, CY = W / 2, R = W / 2 - 26
  const color = palette()[0]!
  const wedge = (k: number) => {
    const n = counts[k] ?? 0
    if (!n) return null
    const r = 10 + (R - 10) * (n / max)
    // Sector k spans angles around its center; 0 = toward the attacked
    // goal, which now sits to the RIGHT (screen y grows downward, so a
    // positive lateral angle sweeps clockwise like the pitch does).
    const a0 = ((k - 0.5) / SECTORS) * 2 * Math.PI
    const a1 = ((k + 0.5) / SECTORS) * 2 * Math.PI
    const p = (a: number, rr: number) =>
      `${CX + rr * Math.cos(a)},${CY + rr * Math.sin(a)}`
    return (
      <path key={k}
            d={`M ${CX},${CY} L ${p(a0, r)} A ${r} ${r} 0 0 1 ${p(a1, r)} Z`}
            fill={color} fillOpacity={0.75} stroke="var(--color-surface-base)"
            strokeWidth={1} />
    )
  }
  return (
    <div className="pg-chart pg-rose">
      <svg viewBox={`0 0 ${W} ${W}`} role="img" aria-label="Direction rose">
        {[0.33, 0.66, 1].map((f) => (
          <circle key={f} cx={CX} cy={CY} r={10 + (R - 10) * f} fill="none"
                  stroke="var(--color-border)" strokeWidth={1} />
        ))}
        {counts.map((_, k) => wedge(k))}
        <text x={W - 4} y={CY - 6} textAnchor="end"
              className="pg-shape-measure">{pitch.attack ? `${pitch.attack.rightward} ` : 'attacking '}&#8594;</text>
        <text x={4} y={CY - 6} textAnchor="start"
              className="pg-timeline__tick">&#8592; own goal</text>
      </svg>
      <p className="pg-note">{segs.length.toLocaleString()} movements;
        {' '}petal length is the share heading that way.</p>
    </div>
  )
}

// ── dispatcher ─────────────────────────────────────────────────────────────

/** The kinds that draw on a pitch (pitchGeom), so read pitch coordinates. */
const PITCH_KINDS = new Set<VisualKind>([
  'pitch', 'heatmap', 'flow', 'pitchzones', 'shape', 'spacemap', 'passnet',
  'rose'])

export function Visual({ kind, result: raw, mapping, pitch: asked, frameColIdx, onFrame }:
  { kind: VisualKind; result: Result; mapping: Mapping; pitch: PitchOptions
    frameColIdx?: number; onFrame?: (frame: number) => void }) {
  // The server's contract outranks the caller's pitch origin: layer-stack
  // rows are corner-origin, and drawn on a centre-origin pitch they land a
  // half-pitch off (Attacking Structure Lab, 2026-09-30). Pitch kinds only
  // — a bar or scatter axis labelled x_m must keep showing x_m.
  const result = PITCH_KINDS.has(kind) ? centreOrigin(raw) : raw
  const pitch = result === raw ? asked : { ...asked, origin: 'center' as const }
  switch (kind) {
    case 'bar': return <BarChart result={result} mapping={mapping} />
    case 'line': return <LineChart result={result} mapping={mapping} />
    case 'scatter': return <ScatterChart result={result} mapping={mapping} />
    case 'pitch': return <PitchMap result={result} mapping={mapping} pitch={pitch} />
    case 'heatmap': return <HeatMapChart result={result} mapping={mapping} pitch={pitch} />
    case 'flow': return <FlowMap result={result} mapping={mapping} pitch={pitch} />
    case 'histogram': return <HistogramChart result={result} mapping={mapping} />
    case 'pitchzones': return <PitchZonesChart result={result} mapping={mapping} pitch={pitch}
                                               frameColIdx={frameColIdx} onFrame={onFrame} />
    case 'shape': return <ShapeChart result={result} mapping={mapping} pitch={pitch} />
    case 'spacemap': return <SpaceMapChart result={result} mapping={mapping} pitch={pitch} />
    case 'timeline': return <TimelineChart result={result} mapping={mapping} />
    case 'passnet': return <PassNetChart result={result} mapping={mapping} pitch={pitch} />
    case 'rose': return <RoseChart result={result} mapping={mapping} pitch={pitch} />
    default: return <DataTable result={result} frameColIdx={frameColIdx} onFrame={onFrame} />
  }
}
